import StarBorderIcon from '@mui/icons-material/StarBorder'
import {
  Avatar,
  Button,
  CardActions,
  CardContent,
  Divider,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
} from '@mui/material'
import { uuid } from 'components/util'
import { useEffect, useState } from 'react'

// project imports
import ImageListItemBar from '@mui/material/ImageListItemBar'
import TitleService from 'title-service'
import MainCard from 'ui-component/cards/MainCard'
import AnimateButton from 'ui-component/extended/AnimateButton'

// assets
import { withStyles } from '@mui/styles'
import RedQuickVideo from 'components/RedQuickVideo'
import { getUserClaims } from 'features/application/application-slice'
import { useSelector } from 'react-redux'
import { UploadThunkAsync } from 'storage-service'
import { dispatch } from 'store'

import { LinearProgress } from '@mui/material'

// style constant
const BorderLinearProgress = withStyles(() => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  bar: {
    borderRadius: 5,
  },
}))(LinearProgress)

export default function ContentCreatorProfileUpload(props: any) {
  let [inputId, setInputId] = useState(`c${uuid()}`)
  let claims = useSelector(getUserClaims)
  const [progressLib, setProgressLib] = useState({})
  const [selected, setSelected] = useState(null)
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [totalItemsToUpload, setTotalItemsToUpload] = useState(0)
  const [progressLibItem, setProgressLibItem] = useState({
    id: null,
    value: null,
  })
  let [pendingFileUploads, setPendingFileUploads] = useState([])
  let [busy, setBusy] = useState(false)
  let [canUpload, setCanUpload] = useState(true)
  let [hasUploaded, setHasUploaded] = useState(false)
  let [invalidAspectRatio, setInvalidAspectRatio] = useState(false)
  let context =
    props.propertyName && props?.context && props?.context[props.propertyName]
      ? props?.context[props.propertyName]
      : null
  let limit = context?.limit || 1
  let uploadOnce = context?.uploadOnce || false
  useEffect(() => {
    if (pendingFileUploads.length) {
      setBusy(true)
    } else {
      setBusy(false)
      if (hasUploaded) {
        if (uploadOnce) {
          setCanUpload(false)
        }
      }
    }
  }, [pendingFileUploads.length, hasUploaded, uploadOnce])
  useEffect(() => {
    if (context?.contextId) {
      setProgressLib({})
    }
  }, [context?.contextId])
  useEffect(() => {
    if (props?.context?.id) {
      setCanUpload(true)
    }
  }, [props?.context?.id])
  useEffect(() => {
    if (progressLibItem?.id) {
      setProgressLib({
        ...progressLib,
        [progressLibItem?.id]: progressLibItem?.value,
      })
    }
  }, [progressLibItem?.id, progressLibItem?.value])

  if (!claims) {
    return <></>
  }
  let content = (
    <>
      <CardContent>
        <Grid item xs={12}>
          {limit > 1 && (
            <ImageList sx={{ maxHeight: 500 }} cols={2}>
              {(props.value || [])
                .map((v) => ({ img: v }))
                .map((item) => (
                  <ImageListItem
                    key={item.img}
                    onClick={() => {
                      setSelected(item.img)
                    }}
                  >
                    <img
                      src={`${item.img}`}
                      srcSet={`${item.img}`}
                      alt={item.title}
                      loading="lazy"
                    />
                    {props.selected === item.img && (
                      <ImageListItemBar
                        sx={{
                          background:
                            'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                            'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                        }}
                        title={TitleService(item.title || 'Selected')}
                        position="top"
                        actionIcon={
                          <IconButton
                            sx={{ color: 'white' }}
                            aria-label={`star ${item.title}`}
                          >
                            <StarBorderIcon />
                          </IconButton>
                        }
                        actionPosition="left"
                      />
                    )}
                  </ImageListItem>
                ))}
            </ImageList>
          )}
          {limit === 1 && context?.video && (
            <RedQuickVideo
              url={props.value}
              onDuration={(duration) => {
                if (context?.onDuration) {
                  context?.onDuration(duration)
                }
              }}
            />
          )}
          {limit === 1 && !context?.video && props.value && context?.image && (
            <ImageList>
              <ImageListItem onClick={() => {}}>
                {' '}
                <img
                  src={`${props.value}`}
                  srcSet={`${props.value}`}
                  loading="lazy"
                />
              </ImageListItem>
            </ImageList>
          )}
          {limit === 1 && !context?.video && !context?.image && (
            <Avatar
              alt="User 1"
              src={props.value}
              sx={{ width: 100, height: 100, margin: '0 auto' }}
            />
          )}
        </Grid>
        {canUpload && !context?.viewOnly && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" align="center">
              {TitleService(
                context?.description || 'Upload/Change Your Profile Image',
              )}
            </Typography>
          </Grid>
        )}
        {totalItemsToUpload ? (
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Typography variant="caption">Overall Progress</Typography>
              </Grid>
              <Grid item xs>
                <BorderLinearProgress
                  variant="determinate"
                  color="secondary"
                  value={(100 * (currentItemIndex + 1)) / totalItemsToUpload}
                />
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  {Math.round(
                    (100 * (currentItemIndex + 1)) / totalItemsToUpload,
                  )}
                  %
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ) : null}
        {Object.keys(progressLib).map((key) => {
          let progress = { value: progressLib[key] }

          if (progress) {
            return (
              <Grid item xs={12} key={key}>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Grid item>
                    <Typography variant="caption">Progress</Typography>
                  </Grid>
                  <Grid item xs>
                    <BorderLinearProgress
                      variant="determinate"
                      color="secondary"
                      value={progress.value}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="h6">
                      {Math.round(progress.value)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )
          }
          return <></>
        })}
        {invalidAspectRatio && (
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Typography variant="caption">
                  {TitleService(`The aspect ratio needs to be 16:9`)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
      <Divider />
      <CardActions>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Grid item>
            <Grid
              container
              alignItems="center"
              justifyContent="flex-end"
              spacing={2}
            >
              {limit > 1 && !context?.viewOnly && (
                <Grid item>
                  <Button
                    disabled={
                      !props.selected ||
                      (props?.value || []).indexOf(props.selected) === -1
                    }
                    onClick={() => {
                      if (props.removeSelected) {
                        props.removeSelected(selected)
                      } else {
                        let updated = props.value.filter((x) => x !== selected)
                        props.onChange([...updated])
                      }
                    }}
                    variant="contained"
                    color="secondary"
                  >
                    {TitleService('Remove Selected')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              alignItems="flex-end"
              justifyContent="flex-end"
              spacing={2}
            >
              {!busy &&
                canUpload &&
                !context?.viewOnly &&
                context?.limit !== 0 &&
                (limit === 1 || limit > (props?.value || [])?.length) && (
                  <Grid item>
                    <AnimateButton>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const fileInput = document.querySelector(
                            `#${inputId}`,
                          )
                          if (fileInput) {
                            ;(fileInput as any).click()
                          }
                        }}
                      >
                        {TitleService(context?.title || 'Upload Avatar')}
                        <input
                          style={{ display: 'none' }}
                          onChange={async () => {
                            if (!busy && canUpload) {
                              const fileInput = document.querySelector(
                                `#${inputId}`,
                              )
                              const files = (fileInput as any).files
                              if (files?.length) {
                                let result = await uploadFiles({
                                  files: files,
                                })
                                await intermediateUpload(result)
                              }
                            }
                          }}
                          accept={context?.accept || 'image/*'}
                          type="file"
                          id={inputId}
                        />
                      </Button>
                    </AnimateButton>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </Grid>
      </CardActions>
    </>
  )
  if (context?.unwrapped) {
    return content
  }

  return (
    <Grid item xs={12}>
      <MainCard
        title={TitleService(
          context?.componentTitle ||
            props.title ||
            props.label ||
            'Profile Picture',
        )}
        content={false}
        style={{
          marginTop: 10,
          marginBottom: 10,
        }}
        contentSX={{ textAlign: 'center' }}
        onDragOver={(ev) => {
          // Prevent default behavior (Prevent file from being opened)
          ev.preventDefault()
        }}
        onDrop={async (ev) => {
          if (!busy) {
            console.log('File(s) dropped')
            // Prevent default behavior (Prevent file from being opened)
            ev.preventDefault()
            handleDrop(ev)
          }
        }}
      >
        {content}
      </MainCard>
    </Grid>
  )

  async function intermediateUpload(
    result: { type: string; blob: any; url: string }[],
  ) {
    if (result.length) {
      let pending = result.slice(0, limit).map((t) => {
        return {
          id: limit > 1 ? uuid() : context?.contextId || uuid(),
          storageLocation:
            context?.storageLocation ||
            `images/${claims.fire_user}/undefined_mediaRel`,
          file: t.blob,
        }
      })
      setPendingFileUploads(
        pending.map((v) => {
          return {
            id: v.id,
            storageLocation: v.storageLocation,
          }
        }),
      )
      await dispatch(
        UploadThunkAsync({
          files: pending,
          onprogress: (args: { id: any; key: 'value'; value: number }) => {
            setProgressLib({
              ...progressLib,
              [args.id]: args.value,
            })
            if (pending.findIndex) {
              let currentIndex = pending.findIndex((v) => v.id === args.id)
              setTotalItemsToUpload(pending.length)
              setCurrentItemIndex(currentIndex)
            }
          },
          callback: (
            b: boolean,
            result: {
              url: string
              id: string
              storageLocation: string
            }[],
          ) => {
            setPendingFileUploads([])
            setHasUploaded(true)
            if (limit === 1) {
              if (
                result &&
                pending[0] &&
                pending[0].id &&
                result[pending[0].id]
              ) {
                props.onChange(
                  result[pending[0].id].url,
                  context.locationProperty
                    ? {
                        [context.locationProperty]:
                          result[pending[0].id].storageLocation,
                      }
                    : {},
                )
                if (
                  context?.onStorageLocation &&
                  result[pending[0].id].storageLocation
                ) {
                  context?.onStorageLocation(
                    result[pending[0].id].storageLocation,
                  )
                }
              }
            } else if (limit > 1) {
              let urls = Object.keys(result)
                .filter((v) => result[v])
                .map((v) => {
                  return result[v].url
                })
              let storageLocations = Object.keys(result)
                .filter((v) => result[v])
                .map((v) => {
                  return result[v].storageLocation
                })
              props.onChange([...(props.value || []), ...urls])
              if (context?.onStorageLocation && storageLocations) {
                context?.onStorageLocation(storageLocations)
              }
            }
          },
        }),
      )
    }
  }
  function dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    let byteString = atob(dataURI.split(',')[1])

    // separate out the mime component
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

    // write the bytes of the string to an ArrayBuffer
    let ab = new ArrayBuffer(byteString.length)

    // create a view into the buffer
    let ia = new Uint8Array(ab)

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    // write the ArrayBuffer to a blob, and you're done
    let blob = new Blob([ab], { type: mimeString })
    return blob
  }
  async function resizeImage(
    file: any,
    max_size: number = 300,
  ): Promise<{ type: string; blob: any; url: string }> {
    // Read in file
    // Ensure it's an image
    return new Promise((resolve, fail) => {
      if (file.type.match(/image.*/)) {
        console.log('An image has been loaded')

        // Load the image
        var reader = new FileReader()
        reader.onload = function (readerEvent) {
          let image: any = new Image()
          image.onload = function () {
            // Resize the image
            var canvas = document.createElement('canvas'),
              width = image.width,
              height = image.height
            if (width > height) {
              if (width > max_size) {
                height *= max_size / width
                width = max_size
              }
            } else {
              if (height > max_size) {
                width *= max_size / height
                height = max_size
              }
            }
            canvas.width = width
            canvas.height = height

            if (
              context?.aspectRatio &&
              context?.aspectRatio !== width / height
            ) {
              setInvalidAspectRatio(true)
              throw 'aspect ratio is in valid'
            } else if (invalidAspectRatio) {
              setInvalidAspectRatio(false)
            }

            canvas.getContext('2d').drawImage(image, 0, 0, width, height)
            var dataUrl = canvas.toDataURL('image/png')
            var resizedImage = dataURItoBlob(dataUrl)
            resolve({
              type: 'imageResized',
              blob: resizedImage,
              url: dataUrl,
            })
          }
          image.onerror = function () {
            fail(new Error('image load failed'))
          }
          image.src = readerEvent.target.result
        }
        reader.onerror = function () {
          fail(new Error('reader failed'))
        }
        reader.readAsDataURL(file)
      }
    })
  }

  async function uploadFiles(args: { files: any[] }) {
    if (args?.files?.length) {
      let filesToUpload: { type: string; blob: any; url: string }[] = []
      for (let i = 0; i < args.files.length; i++) {
        if (args.files[i].type.match(/image.*/)) {
          let data = await resizeImage(
            args.files[i],
            context?.maxSize || 300,
          ).catch(() => {
            console.warn('failed to resize image')
          })
          if (data) {
            filesToUpload.push(data)
          }
        } else if (args.files[i].type.match(/video.*/)) {
          filesToUpload.push({
            type: args.files[i].type,
            blob: args.files[i],
            url: '',
          })
        }
      }
      return filesToUpload
    }
    return []
  }

  async function handleDrop(ev: any) {
    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      let droppedFiles = []
      for (var i = 0; i < ev.dataTransfer.items.length; i++) {
        // If dropped items aren't files, reject them
        if (ev.dataTransfer.items[i].kind === 'file') {
          var file = ev.dataTransfer.items[i].getAsFile()
          droppedFiles.push(file)
        }
      }
      let result = await uploadFiles({
        files: droppedFiles,
      })
      setTotalItemsToUpload(droppedFiles.length)
      await intermediateUpload(result)
    }
  }
}
